import { WorkflowWizardProgress } from "../components/WorkflowWizardProgress";
import { useI18nOptional } from "../i18n/useI18n";
import { lazy, Suspense, useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { Alert, Button, Flex, Space, Typography } from "../ui";
import { PageLoading } from "../components/PageLoading";
import { formatDateDisplay } from "../units/format";
import { spacing } from "../layout/spacing";
import { LockedContentFrame } from "../layout/LockedContentFrame";
import { claimEntryIntent, type EntryIntent } from "./entryIntent";
import { type OnboardingBrand } from "./OnboardingPageFrame";
import { FullPageOverlay } from "./FullPageOverlay";
import { onboardingText } from "./onboardingTranslations";
import { resolveSetupStep } from "./setupState";
import type { SetupAdapter, SetupSnapshot, SetupStep } from "./onboardingTypes";

const SETUP_LATER_STORED_VALUE = "true";
const CONTRACT_STEP_ID = "contract";

const BillingWorkflow = lazy(() => import("../billing/SharedBillingPage").then(module => ({ default: module.SharedBillingPage })));

export interface SharedSetupProps {
  adapter: SetupAdapter;
  /** Includes account and tenant/project. A context change remounts the workflow. */
  scope: string;
  locale: string;
  intentOwner?: string;
  enabled?: boolean;
  /** Routes that stay reachable without a contract (billing, account, legal texts); everything else redirects to the setup page. */
  suspended?: boolean;
  headerExtra?: ReactNode | ((busy: boolean) => ReactNode);
  children: ReactNode | ((openSetup?: () => void) => ReactNode);
  /** Shows the non-blocking hint for pending optional steps on the home page. */
  autoOpen?: boolean;
  refreshToken?: unknown;
  /** Route of the setup page. The product registers it inside its authenticated layout. */
  setupPath?: string;
  /** Dashboard: target when leaving the setup page and place of the setup and contract-end hints. */
  homePath?: string;
  /** Billing page for a running contract that ends (undo cancellation, settle payment). */
  billingPath?: string;
  brand?: OnboardingBrand;
  onLogout?: () => void;
}
export function SharedSetup(props: SharedSetupProps) {
  if (props.enabled === false) return <SetupChildren render={props.children} />;
  return <SetupWorkflow key={props.scope} {...props} />;
}
function SetupWorkflow({ adapter, scope, intentOwner, locale, suspended, children, headerExtra, refreshToken, autoOpen = true,
  setupPath = "/setup", homePath = "/", billingPath, brand, onLogout }: SharedSetupProps) {
  const labels = onboardingText(locale, useI18nOptional()?.germanAddress);
  const location = useLocation();
  const navigate = useNavigate();
  const [snapshot, setSnapshot] = useState<SetupSnapshot | null>(null);
  const [error, setError] = useState(false);
  const [dismissed, setDismissed] = useState(() => {
    try { return window.localStorage.getItem(`mqorva:setup:later:${scope}`) === SETUP_LATER_STORED_VALUE; } catch { return false; }
  });
  const [endNoticeClosed, setEndNoticeClosed] = useState(false);
  const [intent] = useState(() => claimEntryIntent(intentOwner ?? scope));
  const latest = useRef(0);
  const mounted = useRef(false);
  const adapterRef = useRef(adapter);
  useEffect(() => { adapterRef.current = adapter; }, [adapter]);
  const refresh = useCallback(async () => {
    const run = ++latest.current;
    try {
      const next = await adapterRef.current.load();
      if (!mounted.current || run !== latest.current) return;
      setSnapshot(next); setError(false);
    } catch {
      if (mounted.current && run === latest.current) setError(true);
    }
  }, [setSnapshot, setError]);
  useEffect(() => {
    mounted.current = true;
    void refresh();
    return () => { mounted.current = false; };
  }, [refresh, refreshToken]);
  const steps: SetupStep[] = snapshot ? [
    ...snapshot.steps.filter(step => step.required),
    ...(snapshot.contractRequired || !snapshot.contractComplete ? [{
      id: CONTRACT_STEP_ID, title: snapshot.contractLapsed ? labels.renew : labels.contract,
      // Die Tarifwahl führt mit eigener Schrittleiste; ein Dauertext darüber wäre auf jeder Seite derselbe.
      description: snapshot.contractLapsed ? labels.renewHint : undefined,
      state: snapshot.contractComplete ? "done" as const : "pending" as const,
      // A lapsed contract does not redirect: data stays readable, the notice leads to the renewal.
      required: snapshot.contractRequired && !snapshot.contractLapsed,
    }] : []),
    // Optionale Schritte nur, solange sie offen sind — ein erledigter optionaler Schritt ist kein Schritt mehr.
    ...snapshot.steps.filter(step => !step.required && step.state !== "done"),
  ] : [];
  const hasPending = steps.some(step => step.state !== "done");
  const required = steps.some(step => step.required && step.state !== "done");
  const dismiss = (value: boolean) => {
    setDismissed(value);
    try {
      if (value) window.localStorage.setItem(`mqorva:setup:later:${scope}`, "true");
      else window.localStorage.removeItem(`mqorva:setup:later:${scope}`);
    } catch { /* Not a completion marker. */ }
  };
  const openSetup = () => { dismiss(false); navigate(setupPath); };
  if (location.pathname === setupPath) {
    // "Later" is only a display preference for the hint; required steps cannot be postponed.
    const postpone = () => { if (!required) dismiss(true); };
    return <SetupPage snapshot={snapshot} steps={steps} error={error} refresh={refresh} adapter={adapter} intent={intent}
      labels={labels} brand={brand} headerExtra={headerExtra} onLogout={onLogout} postpone={postpone}
      leave={() => { postpone(); navigate(homePath, { replace: true }); }} />;
  }
  if (!suspended && required) return <Navigate to={setupPath} replace />;
  const onHome = location.pathname === homePath;
  return <>
    {snapshot?.contractLapsed && <Alert type="error" showIcon message={labels.lapsedNotice}
      action={snapshot.canManageContract ? <Button size="small" type="primary" onClick={openSetup}>{labels.renew}</Button> : undefined}
      style={{ marginBottom: spacing.sectionGap }} />}
    {onHome && snapshot?.contractEndsAt && !endNoticeClosed && <Alert type="warning" showIcon closable
      message={labels.endingHint.replace("{date}", formatDateDisplay(snapshot.contractEndsAt))}
      action={billingPath ? <Button size="small" onClick={() => navigate(billingPath)}>{labels.renew}</Button> : undefined}
      onClose={() => setEndNoticeClosed(true)} style={{ marginBottom: spacing.sectionGap }} />}
    {onHome && autoOpen && !dismissed && !snapshot?.contractLapsed && hasPending && <Alert type="info" showIcon closable message={labels.pendingHint}
      action={<Button size="small" onClick={openSetup}>{labels.resume}</Button>} onClose={() => dismiss(true)}
      style={{ marginBottom: spacing.sectionGap }} />}
    {/* Nach Tarifende ist der Arbeitsbereich durchgehend rot gerahmt — nicht nur der Hinweis oben. */}
    <LockedContentFrame locked={Boolean(snapshot?.contractLapsed)}>
      <SetupChildren render={children} openSetup={openSetup} />
    </LockedContentFrame>
  </>;
}

type SetupPageProps = Pick<SharedSetupProps, "adapter" | "brand" | "headerExtra" | "onLogout"> & {
  snapshot: SetupSnapshot | null; steps: SetupStep[]; error: boolean; refresh: () => Promise<void>;
  intent: EntryIntent | null; labels: ReturnType<typeof onboardingText>; postpone: () => void; leave: () => void;
};
/** Full page without the app shell: the shell stays mounted for product context, but is covered and inert. */
function SetupPage({ snapshot, steps, error, refresh, adapter, intent, labels, brand, headerExtra, onLogout, postpone, leave }: SetupPageProps) {
  const [busy, setBusy] = useState(false);
  const [selectedId, setSelectedId] = useState<string>();
  const [contractVisited, setContractVisited] = useState(false);
  // Schrittleiste nur, wenn beim Start mehr als ein Schritt offen ist — festgehalten, damit sie nicht
  // verschwindet, sobald unterwegs ein Schritt erledigt wird.
  const [openStepsAtStart, setOpenStepsAtStart] = useState<number | null>(null);
  if (openStepsAtStart === null && snapshot) setOpenStepsAtStart(steps.filter(step => step.state !== "done").length);
  const current = resolveSetupStep(steps, selectedId);
  const hasPending = steps.some(step => step.state !== "done");
  const required = steps.some(step => step.required && step.state !== "done");
  // The billing workflow stays mounted after its first visit so a step change keeps its draft.
  const contractMounted = contractVisited || current?.id === CONTRACT_STEP_ID;
  const select = (id: string | undefined) => {
    if (busy) return;
    if (current?.id === CONTRACT_STEP_ID) setContractVisited(true);
    setSelectedId(id);
  };
  const completeContract = useCallback(async () => { await refresh(); setBusy(false); }, [refresh, setBusy]);
  // Abmelden nur, solange die Einrichtung nicht verlassen werden kann (noch kein Tarif); sonst abbrechen.
  return <FullPageOverlay brand={brand} title={snapshot?.contractLapsed ? labels.renew : labels.title} extra={<Space wrap>
      {typeof headerExtra === "function" ? headerExtra(busy) : headerExtra}
      {required
        ? onLogout && <Button disabled={busy} onClick={onLogout}>{labels.logout}</Button>
        : <Button disabled={busy} onClick={leave}>{labels.cancel}</Button>}
    </Space>}>
      {error && <Alert type="error" showIcon message={labels.loadError} action={<Button onClick={() => refresh()}>{labels.retry}</Button>} />}
      {!snapshot ? !error && <PageLoading /> : <Flex vertical gap={spacing.sectionGap}>
        {!hasPending && <Alert type="success" showIcon message={labels.complete} />}
        {/* Gesamtschritte der Einrichtung (z. B. Firma, Tarif) — nur bei mehreren unabhängigen Schritten.
            Die Tarifwahl hat zusätzlich ihre inneren Schritte im Container. */}
        {(openStepsAtStart ?? 0) > 1 && <WorkflowWizardProgress current={current ? steps.indexOf(current) : steps.length - 1}
          onChange={index => select(steps[index]?.id)}
          items={steps.map(step => ({ title: step.title, disabled: busy || resolveSetupStep(steps, step.id)?.id !== step.id }))} />}
        {current?.description && <Typography.Paragraph style={{ marginBottom: 0 }}>{current.description}</Typography.Paragraph>}
        {contractMounted && snapshot.canManageContract && <div hidden={current?.id !== CONTRACT_STEP_ID}>
          <Suspense fallback={<PageLoading />}><BillingWorkflow {...adapter.billing} setupMode initialEntryIntent={intent} onSetupComplete={completeContract} onSetupBusyChange={setBusy} /></Suspense>
        </div>}
        {current?.id === CONTRACT_STEP_ID && !snapshot.canManageContract && <Alert type="info" showIcon message={labels.ownerRequired} />}
        {current?.state === "waiting" && <Alert type="info" showIcon message={labels.waiting} description={labels.waitingHint} />}
        {snapshot.steps.filter(step => step.content).map(step => <div key={step.id} hidden={current?.id !== step.id}>
          <StepBody render={step.content} refresh={refresh} setBusy={setBusy} />
        </div>)}
        <Space wrap>
          {current && steps.indexOf(current) > 0 && <Button disabled={busy} onClick={() => select(steps[steps.indexOf(current) - 1]?.id)}>{labels.back}</Button>}
          {current?.open && <Button type="primary" disabled={busy} onClick={() => { postpone(); return current.open?.(); }}>{labels.open}</Button>}
          {!required && <Button type={hasPending ? "default" : "primary"} disabled={busy} onClick={leave}>{hasPending ? labels.later : labels.start}</Button>}
        </Space>
      </Flex>}
    </FullPageOverlay>;
}

function StepBody({ render, refresh, setBusy }: { render: SetupStep["content"]; refresh: () => Promise<void>; setBusy: (busy: boolean) => void }) {
  return <>{render?.(refresh, setBusy)}</>;
}

function SetupChildren({ render, openSetup }: { render: SharedSetupProps["children"]; openSetup?: () => void }) {
  return <>{typeof render === "function" ? render(openSetup) : render}</>;
}
